import React, { useEffect, useState } from "react";
import { collection, query, onSnapshot, doc, updateDoc, getDoc, setDoc } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../utils/useAuth";
import { createUserWithEmailAndPassword } from "firebase/auth";
import { auth } from "../firebase";

export default function ManagerReferees() {
  const { user, profile } = useAuth();
  const [referees, setReferees] = useState([]);
  const [selectedReferee, setSelectedReferee] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [editForm, setEditForm] = useState({
    currentLeague: "",
    lawExam: "",
    fitnessExam: "",
    boksmart: "",
    boksmartExpiry: "",
  });
  const [competitions, setCompetitions] = useState([]);
  const [rates, setRates] = useState({
    referee: "",
    assistant1: "",
    assistant2: "",
    assistant4: "",
    assistant5: "",
    timekeeper: "",
    playermovement: "",
    statistician: "",
    technicalzone: "",
  });
  const [competitionRates, setCompetitionRates] = useState({});
  const [refereeMatchFees, setRefereeMatchFees] = useState({});
  const [unions, setUnions] = useState([]);

  const roles = [
    { key: "referee", label: "Referee" },
    { key: "assistant1", label: "AR1" },
    { key: "assistant2", label: "AR2" },
    { key: "assistant4", label: "A4" },
    { key: "assistant5", label: "A5" },
    { key: "timekeeper", label: "Timekeeper" },
    { key: "playermovement", label: "PMR" },
    { key: "statistician", label: "Statistician" },
    { key: "technicalzone", label: "TZM" },
  ];

  // Load all referees
  useEffect(() => {
    const q = query(collection(db, "users"));

    const unsub = onSnapshot(q, (snap) => {
      const allUsers = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const refList = allUsers.filter((u) => u.role === "referee" && u.approved === true);
      setReferees(refList);
    });

    return unsub;
  }, []);

  // Load competitions
  useEffect(() => {
    const unsub = onSnapshot(collection(db, "competitions"), (snap) => {
      const comps = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      comps.sort((a, b) => a.name.localeCompare(b.name));
      setCompetitions(comps);
    });

    return unsub;
  }, []);

  // Load unions
  useEffect(() => {
    const unsub = onSnapshot(collection(db, "unions"), (snap) => {
      const unionList = snap.docs.map((d) => d.data().name);
      setUnions(unionList);
    });

    return unsub;
  }, []);

  // Load billing rates
  useEffect(() => {
    async function loadRates() {
      if (!user) return;
      
      try {
        const ratesDoc = await getDoc(doc(db, "billingRates", user.uid));
        if (ratesDoc.exists()) {
          const data = ratesDoc.data();
          setRates({
            referee: data.referee || "",
            assistant1: data.assistant1 || "",
            assistant2: data.assistant2 || "",
            assistant4: data.assistant4 || "",
            assistant5: data.assistant5 || "",
            timekeeper: data.timekeeper || "",
            playermovement: data.playermovement || "",
            statistician: data.statistician || "",
            technicalzone: data.technicalzone || "",
          });
          setCompetitionRates(data.competitionRates || {});
          setRefereeMatchFees(data.refereeMatchFees || {});
        }
      } catch (err) {
        console.error("Error loading rates:", err);
      }
    }
    
    loadRates();
  }, [user]);

  // When a referee is selected, populate the form
  function selectReferee(ref) {
    setSelectedReferee(ref);
    setEditForm({
      currentLeague: ref.currentLeague || "",
      lawExam: ref.lawExam || "",
      fitnessExam: ref.fitnessExam || "",
      boksmart: ref.boksmart || "",
      boksmartExpiry: ref.boksmartExpiry || "",
    });
  }

  function handleChange(field, value) {
    setEditForm({ ...editForm, [field]: value });
  }

  async function saveChanges() {
    if (!selectedReferee) return;

    try {
      await updateDoc(doc(db, "users", selectedReferee.id), {
        currentLeague: editForm.currentLeague,
        lawExam: editForm.lawExam,
        fitnessExam: editForm.fitnessExam,
        boksmart: editForm.boksmart,
        boksmartExpiry: editForm.boksmartExpiry,
        updatedAt: new Date(),
      });

      alert("Referee information updated successfully!");
      setSelectedReferee(null);
    } catch (err) {
      alert("Error updating referee: " + err.message);
    }
  }

  // Check if BokSmart is valid
  const isBoksmartValid = (expiry) => {
    if (!expiry) return false;
    const expiryDate = new Date(expiry);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return expiryDate >= today;
  };

  // Update referee match fees
  async function updateRefereeMatchFee(competition, role, value) {
    if (!user) return;

    const updatedFees = { ...refereeMatchFees };
    if (!updatedFees[competition]) {
      updatedFees[competition] = {};
    }
    updatedFees[competition][role] = value;

    setRefereeMatchFees(updatedFees);

    try {
      await updateDoc(doc(db, "billingRates", user.uid), {
        refereeMatchFees: updatedFees,
      });
    } catch (err) {
      console.error("Error updating referee match fees:", err);
      alert("Failed to update match fees");
    }
  }

  // CSV Import Function
  async function handleCSVImport(event) {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      const text = e.target.result;
      const lines = text.split('\n').filter(line => line.trim());
      
      // Skip header row
      const dataLines = lines.slice(1);
      
      const results = {
        success: [],
        errors: [],
        total: dataLines.length
      };

      for (const line of dataLines) {
        const [fullName, email, union] = line.split(',').map(s => s.trim());
        
        if (!fullName || !email || !union) {
          results.errors.push(`Invalid row: ${line}`);
          continue;
        }

        // Validate union exists in system
        if (!unions.includes(union)) {
          results.errors.push(`${fullName} - Invalid union "${union}". Must match one of: ${unions.join(', ')}`);
          continue;
        }

        try {
          // Generate a default password (user will need to reset)
          const defaultPassword = "ChangeMe123!";
          
          // Create Firebase Auth user
          const userCredential = await createUserWithEmailAndPassword(auth, email, defaultPassword);
          
          // Create Firestore profile
          await setDoc(doc(db, "users", userCredential.user.uid), {
            fullName,
            email,
            union: union, // Use the validated union from CSV
            role: "referee",
            approved: true, // Auto-approve imports
            createdAt: new Date(),
            requestedRole: "referee"
          });

          results.success.push(fullName);
          
          // Sign out the newly created user to prevent auto-login
          await auth.signOut();
          
        } catch (err) {
          if (err.code === 'auth/email-already-in-use') {
            results.errors.push(`${email} - Email already exists`);
          } else {
            results.errors.push(`${fullName} - ${err.message}`);
          }
        }
      }

      setImportResults(results);
    };

    reader.readAsText(file);
    event.target.value = ''; // Reset input
  }

  function downloadTemplate() {
    const csv = `Full Name,Email,Union\nJohn Smith,john@example.com,${unions[0] || 'Freestate Rugby Referee'}\nJane Doe,jane@example.com,${unions[0] || 'Freestate Rugby Referee'}`;
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'referee_import_template.csv';
    a.click();
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Manage Referees</h2>
        <button
          onClick={() => setShowImportModal(true)}
          className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 font-semibold"
        >
          📥 Import Referees
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* LEFT: REFEREE LIST */}
        <div className="bg-white p-4 rounded shadow">
          <h3 className="text-lg font-bold mb-4">All Referees ({referees.length})</h3>

          <div className="space-y-2 max-h-[600px] overflow-y-auto">
            {referees.map((ref) => {
              const boksmartValid = isBoksmartValid(ref.boksmartExpiry);
              
              return (
                <div
                  key={ref.id}
                  onClick={() => selectReferee(ref)}
                  className={`p-3 border rounded cursor-pointer hover:bg-gray-50 ${
                    selectedReferee?.id === ref.id ? "border-blue-500 bg-blue-50" : ""
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-semibold">{ref.fullName}</p>
                      <p className="text-sm text-gray-600">{ref.email}</p>
                      {ref.currentLeague && (
                        <p className="text-sm text-blue-600 mt-1">
                          League: {ref.currentLeague}
                        </p>
                      )}
                    </div>
                    <div>
                      {boksmartValid ? (
                        <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded">
                          ✓ Valid
                        </span>
                      ) : (
                        <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded">
                          ✕ Expired
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* RIGHT: EDIT FORM */}
        <div className="bg-white p-4 rounded shadow">
          {selectedReferee ? (
            <>
              <h3 className="text-lg font-bold mb-4">
                Edit Manager Fields - {selectedReferee.fullName}
              </h3>

              <div className="space-y-4">
                {/* Current League */}
                <div>
                  <label className="text-sm font-semibold block mb-1">Current League</label>
                  <select
                    className="w-full p-2 border rounded"
                    value={editForm.currentLeague}
                    onChange={(e) => handleChange("currentLeague", e.target.value)}
                  >
                    <option value="">Select</option>
                    <option value="1st League">1st League</option>
                    <option value="2nd League">2nd League</option>
                    <option value="3rd League">3rd League</option>
                    <option value="4th League">4th League</option>
                    <option value="Social">Social</option>
                    <option value="Beginner">Beginner</option>
                  </select>
                </div>

                {/* Law Exam */}
                <div>
                  <label className="text-sm font-semibold block mb-1">Law Exam</label>
                  <input
                    className="w-full p-2 border rounded"
                    value={editForm.lawExam}
                    onChange={(e) => handleChange("lawExam", e.target.value)}
                    placeholder="e.g., Pass, Fail, 85%"
                  />
                </div>

                {/* Fitness Exam */}
                <div>
                  <label className="text-sm font-semibold block mb-1">Fitness Exam</label>
                  <input
                    className="w-full p-2 border rounded"
                    value={editForm.fitnessExam}
                    onChange={(e) => handleChange("fitnessExam", e.target.value)}
                    placeholder="e.g., Pass, Fail, Score"
                  />
                </div>

                {/* BokSmart Number */}
                <div>
                  <label className="text-sm font-semibold block mb-1">BokSmart Number</label>
                  <input
                    className="w-full p-2 border rounded"
                    value={editForm.boksmart}
                    onChange={(e) => handleChange("boksmart", e.target.value)}
                    placeholder="BokSmart certification number"
                  />
                </div>

                {/* BokSmart Expiry */}
                <div>
                  <label className="text-sm font-semibold block mb-1">BokSmart Expiry</label>
                  <input
                    type="date"
                    className="w-full p-2 border rounded"
                    value={editForm.boksmartExpiry}
                    onChange={(e) => handleChange("boksmartExpiry", e.target.value)}
                  />
                  {editForm.boksmartExpiry && (
                    <p className={`text-sm mt-1 ${
                      isBoksmartValid(editForm.boksmartExpiry) 
                        ? "text-green-600" 
                        : "text-red-600"
                    }`}>
                      {isBoksmartValid(editForm.boksmartExpiry) 
                        ? "✓ Valid" 
                        : "✕ Expired"}
                    </p>
                  )}
                </div>

                {/* Buttons */}
                <div className="flex gap-3 pt-4">
                  <button
                    onClick={saveChanges}
                    className="flex-1 bg-green-700 text-white py-2 rounded hover:bg-green-800"
                  >
                    Save Changes
                  </button>
                  <button
                    onClick={() => setSelectedReferee(null)}
                    className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center text-gray-500 py-12">
              <p className="text-lg">Select a referee from the list to edit their information</p>
            </div>
          )}
        </div>
      </div>

      {/* REFEREE MATCH FEES SECTION */}
      <div className="mt-8 bg-white p-6 rounded shadow">
        <h3 className="text-xl font-bold mb-4 border-b pb-2">Referee Match Fees</h3>
        <p className="text-sm text-gray-600 mb-4">
          Set the match fees paid to referees for each competition and role (typically less than school billing rates).
        </p>

        {/* REFEREE MATCH FEES TABLE */}
        {competitions.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border p-2 text-left font-semibold sticky left-0 bg-gray-100 z-10">Competition</th>
                  {roles.map((role) => (
                    <th key={role.key} className="border p-2 text-center font-semibold">
                      {role.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {competitions.map((comp) => (
                  <tr key={comp.id} className="hover:bg-gray-50">
                    <td className="border p-2 font-semibold sticky left-0 bg-white z-10">{comp.name}</td>
                    {roles.map((role) => (
                      <td key={role.key} className="border p-2 text-center">
                        <input
                          type="number"
                          className="w-20 p-1 border rounded text-center"
                          placeholder="0"
                          value={refereeMatchFees[comp.name]?.[role.key] || ""}
                          onChange={(e) => updateRefereeMatchFee(comp.name, role.key, e.target.value)}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-gray-500 text-center py-8">No competitions added yet.</p>
        )}
      </div>

      {/* IMPORT MODAL */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold mb-4">Import Referees from CSV</h3>

            {!importResults ? (
              <>
                <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded">
                  <h4 className="font-semibold text-blue-900 mb-2">Instructions:</h4>
                  <ol className="list-decimal list-inside space-y-1 text-sm text-blue-800">
                    <li>Download the CSV template below</li>
                    <li>Fill in referee details (Full Name, Email, Union)</li>
                    <li><strong>Important:</strong> Union must exactly match one of these: <span className="font-semibold">{unions.join(', ')}</span></li>
                    <li>Upload the completed CSV file</li>
                    <li>Referees will be created with default password: <span className="font-mono bg-blue-100 px-1">ChangeMe123!</span></li>
                    <li>Referees will be auto-approved and can login immediately</li>
                  </ol>
                </div>

                <div className="mb-4">
                  <button
                    onClick={downloadTemplate}
                    className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 font-semibold"
                  >
                    📥 Download CSV Template
                  </button>
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-semibold mb-2">Upload CSV File</label>
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleCSVImport}
                    className="w-full p-2 border rounded"
                  />
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => setShowImportModal(false)}
                    className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded font-semibold hover:bg-gray-400"
                  >
                    Cancel
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="mb-4">
                  <h4 className="font-bold text-lg mb-2">Import Results</h4>
                  <p className="text-sm text-gray-600 mb-4">
                    Total rows processed: {importResults.total}
                  </p>

                  {importResults.success.length > 0 && (
                    <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded">
                      <h5 className="font-semibold text-green-900 mb-2">
                        ✅ Successfully imported ({importResults.success.length}):
                      </h5>
                      <ul className="list-disc list-inside text-sm text-green-800">
                        {importResults.success.map((name, idx) => (
                          <li key={idx}>{name}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {importResults.errors.length > 0 && (
                    <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded">
                      <h5 className="font-semibold text-red-900 mb-2">
                        ❌ Errors ({importResults.errors.length}):
                      </h5>
                      <ul className="list-disc list-inside text-sm text-red-800">
                        {importResults.errors.map((error, idx) => (
                          <li key={idx}>{error}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                <button
                  onClick={() => {
                    setShowImportModal(false);
                    setImportResults(null);
                  }}
                  className="w-full bg-blue-600 text-white py-2 px-4 rounded font-semibold hover:bg-blue-700"
                >
                  Close
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

